/**
 * Command for moving the dimension line of a linear dimension.
 *
 * Unlike TransformEntityCommand which moves the entire entity, this only moves
 * the dimension line: the measured points stay where they are, so the
 * dimension keeps measuring the same thing.
 */

import type { ICommand } from "./ICommand";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import { deleteObj } from "../core/delete";
import { findEntityByHandle } from "../core/oda";
import type { Point3D } from "../transform/types";
import { oplogEmitter, type OplogEmit } from "../meta/oplog";
import { setDimensionLinePoint } from "../dimensions/creation";

export interface MoveDimensionLineParams {
  /** Handle of the dimension entity */
  entityHandle: string;
  /** Original dimension line point */
  fromPoint: Point3D;
  /** New dimension line point */
  toPoint: Point3D;
}

export class MoveDimensionLineCommand implements ICommand {
  private viewer: DRDrawingViewer;
  private params: MoveDimensionLineParams;
  private executed = false;
  private oplogEvents: OplogEmit[] = [];

  constructor(viewer: DRDrawingViewer, params: MoveDimensionLineParams) {
    this.viewer = viewer;
    this.params = params;
  }

  /**
   * Mark as executed without applying the move again: the handle already
   * moved the dimension line while it was being dragged.
   */
  markAsExecuted(): void {
    this.executed = true;
  }

  /**
   * L'oplog doit être émis même si `markAsExecuted()` a déjà été appelé (le guard
   * `executed` ne doit pas le court-circuiter) — CommandHistory n'appelle execute()
   * qu'une seule fois de toute façon.
   */
  execute(): void {
    this.oplogEvents = oplogEmitter.emitModified(this.viewer, [
      { handle: this.params.entityHandle, changeKind: "moved" },
    ]);
    if (this.executed) return;
    this.setDimLinePoint(this.params.toPoint);
    this.executed = true;
  }

  undo(): void {
    if (!this.executed) return;
    this.setDimLinePoint(this.params.fromPoint);
    this.executed = false;
    this.viewer.update();
    oplogEmitter.revert(this.oplogEvents);
  }

  redo(): void {
    if (this.executed) return;
    this.setDimLinePoint(this.params.toPoint);
    this.executed = true;
    this.viewer.update();
    oplogEmitter.reemit(this.oplogEvents);
  }

  dispose(): void {
    // Nothing to clean up
  }

  private setDimLinePoint(point: Point3D): void {
    const db = this.viewer.database;
    if (!db) return;

    const module = this.viewer.module;
    const entityId = findEntityByHandle(module, db, this.params.entityHandle);
    if (!entityId) return;

    setDimensionLinePoint(module, entityId, point);
    deleteObj(entityId);
  }
}
